import { Box, Radio } from "@outschool/backpack";
import { Search } from "@outschool/business-rules";
import { useLocale, useTranslation } from "@outschool/localization";
import { dayjs, timeZoneName } from "@outschool/time";
import {
  DayPickerLocalized,
  filterToDayjs,
  useTranslateRelativeDateFilter,
} from "@outschool/ui-components-website";
import {
  Checkbox,
  Input,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React from "react";

import { useTimeZone } from "../../../../providers/TimeZoneProvider";
import { FILTER_FORMAT, parseDate } from "../../common";
import { filterDateFilters, filterToDayPicker } from "../../DateFilterHelpers";
import { Filter, FilterList } from "../Filter";
import { ListingChildFilterProps } from "./ListingFilters";

const DISPLAY_FORMAT = "ll";

export default function DateFilter({
  showTopBorder = false,
  searchFilters,
  onChange,
}: ListingChildFilterProps) {
  const dates = _.pick(searchFilters, [
    ...Search.DATE_FILTERS,
    "includeInProgressFixedLengthLiveFormat",
  ]);

  if (!dates.hasOwnProperty("startAfter")) {
    dates.startAfter = Search.RelativeDateFilter.AllUpcoming;
  }
  if (!dates.hasOwnProperty("includeInProgressFixedLengthLiveFormat")) {
    dates.includeInProgressFixedLengthLiveFormat = true;
  }

  const [selectingNext, setSelectingNext] = React.useState<string>();
  const { timeZone } = useTimeZone();
  const fromInputRef = React.useRef<HTMLInputElement>();
  const toInputRef = React.useRef<HTMLInputElement>();
  const preventAutoFocusInputRef = React.useRef<HTMLInputElement>(null);
  const { datePickerFrom, datePickerTo } = filterToDayPicker(
    // @ts-ignore TS(2345): Argument of type 'InputMaybe<string> | undefined' ... Remove this comment to see the full error message
    dates.startAfter,
    dates.startBefore,
  );

  const [impressionTrackingNode, setImpressionTrackingNode] =
    React.useState(null);
  const { trackTouch } = useImpressionTracking({
    node: impressionTrackingNode,
    trackingLabel: "date_filter",
    uniqueId: "date_filter",
  });

  const performUpdate = (updatedFilter: $TSFixMe) => {
    const filters = updatedFilter();
    // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    onChange(filters);
  };

  // set focus on first date checkbox element (Today) to address mobile ios
  // bug that focuses on a hidden text input element
  React.useEffect(() => {
    // perform a simple focus blur to prevent autofocus
    preventAutoFocusInputRef?.current?.focus();
    preventAutoFocusInputRef?.current?.blur();
  }, [preventAutoFocusInputRef]);

  const onDayClick = (day: $TSFixMe) => {
    const dayjsDay = dayjs(day).startOf("day");
    const setFirst = selectingNext
      ? selectingNext === "from"
      : !datePickerFrom ||
        (dayjsDay.isBefore(dayjs(datePickerFrom)) && datePickerTo) ||
        dayjsDay.isSame(dayjs(datePickerTo));

    const days = [
      setFirst ? dayjsDay : dayjs(datePickerFrom),
      setFirst ? dayjs(datePickerTo) : dayjsDay,
    ];

    const isInvalid = days[0].isAfter(days[1]);

    if (setFirst) {
      setSelectingNext("to");
      if (toInputRef.current) {
        toInputRef.current.focus();
      }

      if (isInvalid) {
        // @ts-ignore TS(2322): Type 'null' is not assignable to type 'Dayjs'.
        days[1] = null;
      }
    } else {
      setSelectingNext(undefined);
      if (isInvalid) {
        days[0] = days[1];
      }
    }

    performUpdate((filters: $TSFixMe) => ({
      ...filters,
      startAfter: days[0]?.format(FILTER_FORMAT),
      startBefore: days[1]?.format(FILTER_FORMAT),
    }));
    trackTouch();
  };

  const today = parseDate(dayjs().startOf("day"));
  const isDateRange =
    [
      Search.RelativeDateFilter.Today,
      Search.RelativeDateFilter.SevenDays,
      Search.RelativeDateFilter.FourteenDays,
      Search.RelativeDateFilter.Summer,
      Search.RelativeDateFilter.Fall,
      Search.RelativeDateFilter.January,
    ].some(val => val === dates.startAfter) || !!dates.startBefore;

  const locale = useLocale();
  const { t } = useTranslation(
    "client\\components\\search\\mobile\\filters\\DateFilter",
  );
  const translateRelativeDateFilter = useTranslateRelativeDateFilter();
  const allDaysString = t("All upcoming dates");
  const filtersToShow = filterDateFilters(Search.RELATIVE_DATE_FILTERS);

  return (
    <FilterList ref={setImpressionTrackingNode}>
      <Filter
        showTopBorder={showTopBorder}
        sectionTitle={t("Class start date ({{timeZone}})", {
          timeZone: timeZoneName(timeZone),
        })}
      >
        <Box
          sx={{
            padding: "12px 24px 24px 0px",
            fontSize: "1em",
          }}
        >
          {filtersToShow.map(
            (relativeDateFilter: Search.RelativeDateFilter) => (
              <label key={relativeDateFilter} style={{ cursor: "pointer" }}>
                <Radio
                  sx={{ marginRight: "small" }}
                  checked={dates.startAfter === relativeDateFilter}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const checked = e.target.checked;
                    performUpdate((filters: $TSFixMe) => ({
                      ...filters,

                      startAfter: checked
                        ? relativeDateFilter
                        : filters.startAfter,

                      startBefore: checked ? null : filters.startBefore,
                    }));
                  }}
                  // send preventAutoFocusInputRef along with "Today" value to
                  // get around mobile ios hidden text input focus bug
                  inputRef={
                    relativeDateFilter === "Today"
                      ? preventAutoFocusInputRef
                      : null
                  }
                />
                {translateRelativeDateFilter(relativeDateFilter)}
              </label>
            ),
          )}
          <label htmlFor="date-custom-range">
            <Radio
              id="date-custom-range"
              sx={{ marginRight: "small" }}
              checked={
                !_.values(Search.RelativeDateFilter).some(
                  val => val === dates.startAfter,
                )
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.checked) {
                  const { startDayjs, endDayjs } = filterToDayjs(
                    dates.startAfter!,
                    dates.startBefore || null,
                  );
                  performUpdate((filters: $TSFixMe) => ({
                    ...filters,
                    startAfter: startDayjs?.format(FILTER_FORMAT),
                    startBefore: endDayjs?.format(FILTER_FORMAT),
                  }));
                }
              }}
            />
            {t("Custom dates")}
          </label>
          <Checkbox
            key={"includeInProgressFixedLengthLiveFormat"}
            label={t("Include in-progress classes")}
            name={t("Include in-progress classes")}
            sx={{ marginLeft: "10px", marginTop: "10px" }}
            checked={Boolean(dates.includeInProgressFixedLengthLiveFormat)}
            onChange={e => {
              const checked = e.target.checked;
              performUpdate((filters: $TSFixMe) => ({
                ...filters,
                ...dates,
                includeInProgressFixedLengthLiveFormat: checked,
              }));
            }}
          />
        </Box>

        <Box
          flex
          sx={{
            backgroundColor: "#f8f8f9",
            justifyContent: "center",
            marginX: "-1em",
          }}
        >
          <Box
            flex
            sx={{
              flexDirection: "column",
              alignItems: "stretch",
              justifyContent: "center",
              marginBottom: "1em",
              padding: "1em",
            }}
          >
            <DayPickerLocalized
              locale={locale}
              selected={{
                from: datePickerFrom,
                to: datePickerTo,
              }}
              // @ts-ignore TS(2322): Type '{ before: Date | undefined; }' is not assign... Remove this comment to see the full error message
              disabled={{ before: today }}
              modifiers={{
                // @ts-ignore TS(2322): Type 'false | (Date | { after: Date | undefined; }... Remove this comment to see the full error message
                future: !datePickerTo && [today, { after: today }],
              }}
              modifiersClassNames={{
                future: "rdp-day_future",
              }}
              onDayClick={onDayClick}
            />
            <Box
              flex
              sx={{
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "1em",
              }}
            >
              <Input
                sx={{ maxWidth: 150 }}
                value={dayjs(datePickerFrom).format(DISPLAY_FORMAT)}
                ref={fromInputRef}
                onFocus={e => {
                  e.target.select();
                  setSelectingNext("from");
                }}
                onChange={() => {}}
              />
              {/* eslint-disable-next-line i18next/no-literal-string */}
              {"-"}
              <Input
                sx={{ maxWidth: 150 }}
                value={
                  datePickerTo
                    ? dayjs(datePickerTo).format(DISPLAY_FORMAT)
                    : allDaysString
                }
                disabled={!datePickerTo}
                ref={toInputRef}
                onFocus={e => {
                  e.target.select();
                  setSelectingNext("to");
                }}
                onChange={() => {}}
              />
            </Box>
            <Box
              flex
              sx={{
                flexDirection: "column",
              }}
            >
              <label htmlFor="day-selected-only">
                <Radio
                  id="day-selected-only"
                  sx={{ marginRight: "small" }}
                  checked={isDateRange}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const checked = e.target.checked;
                    performUpdate((filters: $TSFixMe) => ({
                      ...filters,

                      startAfter:
                        checked &&
                        filters.startAfter ===
                          Search.RelativeDateFilter.AllUpcoming
                          ? dayjs().startOf("day").format(FILTER_FORMAT)
                          : filters.startAfter,

                      startBefore:
                        checked &&
                        filters.startAfter ===
                          Search.RelativeDateFilter.AllUpcoming
                          ? dayjs().startOf("day").format(FILTER_FORMAT)
                          : filters.startBefore || filters.startAfter,
                    }));
                  }}
                />
                {t("Selected dates only")}
              </label>
              <label htmlFor="day-selected-and-later">
                <Radio
                  id="day-selected-and-later"
                  sx={{ marginRight: "small" }}
                  checked={!isDateRange}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const checked = e.target.checked;
                    const { startDayjs } = filterToDayjs(
                      dates.startAfter!,
                      dates.startBefore || null,
                    );
                    performUpdate((filters: $TSFixMe) => ({
                      ...filters,

                      startAfter: checked
                        ? startDayjs.format(FILTER_FORMAT)
                        : filters.startAfter,

                      startBefore: checked ? null : filters.startBefore,
                    }));
                  }}
                />
                {t("Selected date and later")}
              </label>
            </Box>
          </Box>
        </Box>
      </Filter>
    </FilterList>
  );
}
